import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLazyGetVerifiedUsersBirthdayQuery } from '../features/apis/authApi';
import useCurrentUser from './useCurrentUser';
import { useLazyGetEventsByCompanyQuery } from '../features/apis/intergration';
import { FaBirthdayCake, FaUmbrellaBeach, FaChurch } from 'react-icons/fa';
import { MdEvent } from 'react-icons/md';

interface Holiday {
  name: string;
  description?: string;
  date: Date | string;
  country: { id: string; name: string };
}

const holidayIcons: Record<string, JSX.Element> = {
  birthday: <FaBirthdayCake className="w-6 h-6 text-pink-500" />,
  holiday: <FaUmbrellaBeach className="w-6 h-6 text-green-500" />,
  event: <MdEvent className="w-6 h-6 text-blue-500" />,
  religious: <FaChurch className="w-6 h-6 text-purple-500" />,
};

export const useUserHolidays = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [birthdays, setBirthdays] = useState<any[]>([]);
  const [companyEvents, setCompanyEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const user = useCurrentUser();

  const [getVerifiedBirthdays] = useLazyGetVerifiedUsersBirthdayQuery();
  const [getEvents] = useLazyGetEventsByCompanyQuery();
  const refetchData = useCallback(async () => {
    if (!user?.companyId) return;
    setLoading(true);
    setError(null);

    try {
      const birthdayRes = await getVerifiedBirthdays(user?.companyId).unwrap();
      setBirthdays(birthdayRes?.employees || []);
      const locRes = await fetch('https://api.country.is/');
      const locData = await locRes.json();
      const countryCode = locData.country || 'US';
      const year = new Date().getFullYear();
      const eventsResult = await getEvents({
        companyId: user.companyId,
        countryCode,
        year,
      }).unwrap();

      setHolidays(eventsResult?.holidays || []);
      setCompanyEvents(eventsResult?.events || []);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [getVerifiedBirthdays, getEvents, user?.companyId]);
  useEffect(() => {
    refetchData();
  }, [refetchData]);

  const getThisMonthHolidaysSummary = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const filtered = holidays?.filter((holiday) => {
      const parsed = new Date(holiday.date);
      if (isNaN(parsed.getTime())) return false;

      return (
        parsed.getFullYear() === currentYear &&
        parsed.getMonth() + 1 === currentMonth
      );
    });

    return {
      total: filtered.length,

      holidays: filtered
        .map((holiday) => {
          const parsed = new Date(holiday.date);
          const day = String(parsed.getDate()).padStart(2, '0');
          return `${day} ${holiday.name}`;
        })
        .sort(),
    };
  };
  const getUpcomingHolidaysSummary = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);

    const upcomingEvents = (
      companyEvents?.filter((event: any) => {
        const parsed = new Date(event.start);
        if (isNaN(parsed.getTime())) return false;
        return parsed >= now && parsed <= endOfYear;
      }) || []
    ).map((event: any) => ({
      id: event._id,
      title: event.title || 'Event',
      description: event.description || '',
      start: event.start,
      end: event.end || event.start,
      type: event.type || 'event',
    }));

    const upcomingHoliday = (
      holidays?.filter((holiday: any) => {
        const parsed = new Date(holiday.date?.iso);
        if (isNaN(parsed.getTime())) return false;
        return parsed >= now && parsed <= endOfYear;
      }) || []
    ).map((holiday: any) => ({
      name: holiday.name || 'Holiday',
      description: holiday.description || '',
      date: holiday.date?.iso || '',
    }));

    const formattedHolidays = upcomingHoliday
      .map((holiday) => {
        const parsed = new Date(holiday.date);
        const day = String(parsed.getDate()).padStart(2, '0');
        return `${day} ${holiday.name}`;
      })
      .sort();

    return {
      totalUpcoming: upcomingHoliday.length + upcomingEvents.length,
      upcomingEvents,
      upcomingHoliday,
      formattedHolidays,
    };
  };

  const combinedEvents = useMemo(
    () => [
      ...birthdays.map((user: any) => ({
        id: `birthday-${user._id}`,
        title: user.name,
        start: user.dob,
        end: user.dob,
        type: 'birthday',
        avatar: holidayIcons['birthday'],
        description: 'Birthday',
        isEditDelete: false,
      })),
      ...holidays.map((holiday: any, i: number) => {
        const parsedDate = new Date(holiday.date?.iso);
        const isoDate = !isNaN(parsedDate.getTime())
          ? parsedDate.toISOString().split('T')[0]
          : holiday.date?.iso || '';

        return {
          id: `holiday-${i}`,
          title: holiday.name || 'Holiday',
          start: isoDate,
          end: isoDate,
          type: 'holiday',
          avatar: holidayIcons['holiday'],
          description: holiday.description || '',
          isEditDelete: false,
        };
      }),

      ...companyEvents.map((event: any) => ({
        id: event._id,
        title: event.title || 'Event',
        start: event.start,
        end: event.end || event.start,
        type: event.type || 'event',
        avatar: holidayIcons[event.type] || holidayIcons['event'],
        description: event.description || '',
        isEditDelete: true,
      })),
    ],
    [birthdays, holidays, companyEvents]
  );

  return {
    holidays,
    birthdays,
    companyEvents,
    loading,
    error,
    combinedEvents,
    getThisMonthHolidaysSummary,
    getUpcomingHolidaysSummary,
    refetchData,
  };
};
